import { getRoomWallPositions } from "building-generation/elements/getRoomWallPositions";
import type { Casework } from "building-types/casework/caseworkTypes";
import type { ReferenceGeom } from "building-types/entities/referenceGeom";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { Position, PositionWithLevel, RoomPosition } from "building-types/rooms/roomPosition";
import { RoomPositionType } from "building-types/rooms/roomPosition";
import type { Room } from "building-types/rooms/roomTypes";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import { assertIsNotNull, assertIsNotUndefined } from "common/utils/assertOfType";
import { degreeToRadian } from "common/utils/math";
import objectFilter from "common/utils/objectFilter";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import type { MergedPropLibrary } from "doc/propTools";
import { normalizeEntityPointToPolyLine2 } from "doc/rooms/normalizeEntityPointToPolyLine2";
import { propBBAdjustments } from "doc/utils/propTools";
import { aabb2Center } from "geom/aabb2";
import { aabb3Union, aabb3XLength, aabb3YLength } from "geom/aabb3";
import type { AABB3 } from "geom/aabb3Types";
import { brepAABB3 } from "geom/brep";
import { extrusionToBRep } from "geom/extrude";
import { ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { polyLine2ByTwoPoints, polyLine2EdgeEval, polyLine2EdgeLength, polyLine2EdgeNormal } from "geom/polyLine2";
import { vec2Perp, vec2Rotate, vec2Scale } from "geom/vec2";
import { getCombinedMirrorAxes, isCombinedGlobalInstanceMirror } from "../generationTools/generateOpeningsTools";

export async function getRoomPositions({
  roomPosition,
  room,
  docRefGeom,
  props,
  casework,
  propCatalog,
  mergedPropLibrary,
  mirror,
  roomOutlines,
  wallMasses,
  roomIndex,
  wallLayerExtentsCache,
}: {
  roomPosition: RoomPosition;
  room: Room;
  docRefGeom: Table<ReferenceGeom>;
  props: Table<Prop>;
  casework: Table<Casework>;
  propCatalog: PropAttributeTable;
  mergedPropLibrary: MergedPropLibrary;
  mirror: boolean;
  roomOutlines: Table<RoomOutlines>;
  wallMasses: Table<WallMassSystem>;
  roomIndex: RoomIndex;
  wallLayerExtentsCache: WallLayerExtentsCache;
}): Promise<PositionWithLevel[]> {
  if (!room || !roomOutlines[room.id].INSIDE_OF_MASS_IGNORE_VOID || !roomPosition) {
    return [];
  }

  const insideFramingOutline = roomOutlines[room.id].INSIDE_OF_MASS_IGNORE_VOID;
  const effectiveMirror = isCombinedGlobalInstanceMirror(mirror, room);
  const { combinedFlipX, combinedFlipY } = getCombinedMirrorAxes(mirror, room.componentTransform);

  switch (roomPosition.type) {
    case RoomPositionType.WALL: {
      return getRoomWallPositions(
        room,
        roomPosition,
        effectiveMirror,
        roomOutlines[room.id],
        wallMasses,
        roomIndex,
        wallLayerExtentsCache,
      );
    }
    case RoomPositionType.REF_GEOM: {
      const { geomId, paramNormalized, offset, offsetNormalizedToRefGeomWidth } = roomPosition;
      let { param, rotation } = roomPosition;

      const refGeom = geomId !== undefined && geomId !== null ? docRefGeom[geomId] : undefined;

      if (!refGeom) {
        return [];
      }

      let edgeId = roomPosition.edgeId;
      assertIsNotNull(edgeId);

      if (mirror) {
        if (edgeId !== 0) {
          edgeId = refGeom.geom.length / 2 - 1 - edgeId;
        }
        if (rotation !== undefined) {
          rotation = -rotation;
        }
        param = paramNormalized ? 1 - param : polyLine2EdgeLength(refGeom.geom, edgeId) - param;
      }

      const [x, y] = polyLine2EdgeEval(refGeom.geom, edgeId, param, paramNormalized);
      const [nx, ny] = polyLine2EdgeNormal(refGeom.geom, edgeId, true);
      const [rnx, rny] = rotation !== undefined ? vec2Rotate(nx, ny, degreeToRadian(rotation)) : [nx, ny];

      if (offsetNormalizedToRefGeomWidth) {
        // cast ray from edge to opposite edge
        const ints = intersectRay2PolyLine2(refGeom.geom, x, y, nx, ny);
        ray2PolyLine2IntersectionMakeUnique(ints);
        if (ints.length < 2) {
          return [
            {
              location: [x, y],
              normal: [rnx, rny],
              levelId: room.level.id,
            },
          ];
        }
        const { x: intx, y: inty } = ints[1];
        const [ox, oy] = polyLine2EdgeEval(polyLine2ByTwoPoints(x, y, intx, inty), 0, offset, true);
        return [
          {
            location: [ox, oy],
            normal: [rnx, rny],
            levelId: room.level.id,
          },
        ];
      }

      const [snx, sny] = vec2Scale(nx, ny, offset);
      return [
        {
          location: [x + snx, y + sny],
          normal: [rnx, rny],
          levelId: room.level.id,
        },
      ];
    }
    case RoomPositionType.PROP: {
      const { propType, entityPlaneXNormToRoomWidth, entityPlaneYNormToRoomWidth, entityPlaneXOff, entityPlaneYOff } =
        roomPosition;
      let { rotation } = roomPosition;

      if (effectiveMirror && rotation !== undefined) {
        rotation = -rotation;
      }

      const propMatches = objectFilter(props, (prop) => prop.src === propType);
      const positions: Position[] = [];
      for (const propId in propMatches) {
        const prop = propMatches[propId];
        const [tx, ty] = vec2Rotate(0, 1, degreeToRadian(prop.rotationDegrees));
        const scaleXSign = prop.scaleX < 0 ? -1 : 1;
        const [perpX, perpY] = vec2Perp(tx, ty);
        const [nx, ny] = [perpX * scaleXSign, perpY * scaleXSign];
        const { xAdj, yAdj } = await propBBAdjustments(propCatalog, prop, mergedPropLibrary);
        const [xWithAdjust, yWithAdjust] = vec2Rotate(xAdj, -yAdj, degreeToRadian(prop.rotationDegrees));
        const [bbx, bby] = [prop.x + xWithAdjust, prop.y + yWithAdjust];
        assertIsNotUndefined(insideFramingOutline);

        const position = normalizeEntityPointToPolyLine2({
          outline: insideFramingOutline,
          bbx,
          bby,
          entityPlaneXOffset: entityPlaneXOff,
          entityPlaneXNormalizedToRoomWidth: entityPlaneXNormToRoomWidth,
          entityPlaneYOffset: entityPlaneYOff,
          entityPlaneYNormalizedToRoomWidth: entityPlaneYNormToRoomWidth,
          tx: tx * prop.scaleY,
          ty: ty * prop.scaleY,
          nx,
          ny,
        });
        if (position) {
          const {
            normal: [pnx, pny],
          } = position;
          const [rnx, rny] = rotation !== undefined ? vec2Rotate(pnx, pny, degreeToRadian(rotation)) : [pnx, pny];
          positions.push({ ...position, normal: [rnx, rny] });
        }
      }

      return positions.map((x) => {
        return { ...x, levelId: room.level.id };
      });
    }
    case RoomPositionType.CASEWORK: {
      const {
        caseworkName,
        entityPlaneXOffset,
        entityPlaneXNormalizedToRoomWidth,
        entityPlaneYOffset,
        entityPlaneYNormalizedToRoomWidth,
      } = roomPosition;

      let { rotation } = roomPosition;

      if (effectiveMirror && rotation !== undefined) {
        rotation = -rotation;
      }

      const caseworkMatches = objectFilter(casework, (cwk) => !!(cwk.name && cwk.name === caseworkName));
      const positions: Position[] = [];
      for (const id in caseworkMatches) {
        const { decorativeExtrusions, wallSweepExtrusions, breps } = caseworkMatches[id];
        const extrusions = [...decorativeExtrusions, ...wallSweepExtrusions];
        if (extrusions.length < 1 && breps.length < 1) {
          continue;
        }

        let bb: AABB3;
        if (extrusions.length > 0) {
          bb = brepAABB3(extrusionToBRep(extrusions[0]));
        } else {
          bb = brepAABB3(breps[0]);
        }

        for (const ext of extrusions) {
          bb = aabb3Union(bb, brepAABB3(extrusionToBRep(ext)));
        }

        for (const brep of breps) {
          bb = aabb3Union(bb, brepAABB3(brep));
        }

        const xDim = aabb3XLength(bb);
        const yDim = aabb3YLength(bb);
        const [cx, cy] = aabb2Center(bb);
        const rotationAngle = xDim > yDim ? -90 : 0;

        let x = entityPlaneXOffset;
        let y = entityPlaneYOffset;

        if (combinedFlipX) {
          xDim > yDim ? (x = -x) : (y = -y);
        }
        if (combinedFlipY) {
          xDim > yDim ? (y = -y) : (x = -x);
        }

        const [tx, ty] = vec2Rotate(0, 1, degreeToRadian(rotationAngle));
        const [enx, eny] = vec2Perp(tx, ty);
        const [nx, ny] = rotation !== undefined ? vec2Rotate(enx, eny, degreeToRadian(rotation)) : [enx, eny];

        assertIsNotUndefined(insideFramingOutline);
        const position = normalizeEntityPointToPolyLine2({
          outline: insideFramingOutline,
          bbx: cx,
          bby: cy,
          entityPlaneXOffset: x,
          entityPlaneYOffset: y,
          entityPlaneXNormalizedToRoomWidth,
          entityPlaneYNormalizedToRoomWidth,
          tx,
          ty,
          nx,
          ny,
        });
        if (position) {
          const {
            normal: [pnx, pny],
          } = position;
          const [rnx, rny] = rotation !== undefined ? vec2Rotate(pnx, pny, degreeToRadian(rotation)) : [pnx, pny];
          positions.push({ ...position, normal: [rnx, rny] });
        }
      }

      return positions.map((x) => {
        return { ...x, levelId: room.level.id };
      });
    }
  }
}
